import { broadcastChannelMessages } from '@/composables/helpers'
import type { IAccosApiResponse, IAccosBarDto } from '@/composables/interfaces/apiInterfaces'
import type { IFiltersDataInterface, IFilterStoreInterface } from '@/composables/interfaces/appInterfaces'
import { useAccosBarApi } from '@/composables/useApi'
import { FiltersLocalStorage, PetsAllowedAmenityId } from '@/constants'
import { watchIgnorable } from '@vueuse/core'
import { ref, watch, type Ref } from 'vue'
import { accosBarData, location, pets } from './accosbar.store'

export const ratingsFilterSelected: Ref<number[]> = ref([])
export const filterCount = ref(0)
export const searchedAreaId = ref()
export const filtersData = ref<IFiltersDataInterface | null>(null)
export const accosApiResults = ref<IAccosApiResponse | null>(null)

export const filterStore = ref<IFilterStoreInterface>({
  areaId: 0,
  startDate: null,
  endDate: null,
  rooms: null,
  accotypeIds: [],
  themeId: 0,
  amenityIds: [],
  boardingIds: [],
  ratingFrom: 0,
  ratingTo: 5.5,
  companyIds: []
})

export const watchFilterStore = watchIgnorable(
  filterStore,
  () => {
    setFilterStoreLocalStorage()
    emitToOtherApps()
  },
  { deep: true } //ensures that the watcher reacts to changes in nested properties inside objects or arrays.
)

export const broadcasterFilterStore = new BroadcastChannel(FiltersLocalStorage)
export const messageReceiverFilterStore = new BroadcastChannel(FiltersLocalStorage)

function emitToOtherApps(): void {
  broadcasterFilterStore.postMessage(broadcastChannelMessages.UPDATE)
}

messageReceiverFilterStore.addEventListener('message', async (e) => {
  switch (e.data) {
    case broadcastChannelMessages.UPDATE:
      initFilterStoreFromLocalStorage()
      break
    case broadcastChannelMessages.ACCOSBARUPDATE:
      updateFilterStoreFromAccosBarStore()
      break
    case broadcastChannelMessages.REFRESHLOCATIONFROMFILTERSTORE:
      updateLocationFromFilterStore()
      break
    default:
      console.warn(`NO HANDLER FOR MESSAGE: ${e.data}`)
  }
})

function setFilterStoreLocalStorage(): void {
  localStorage.setItem(
    FiltersLocalStorage,
    JSON.stringify({
      ...filterStore.value,
      accotypes: filterStore.value.accotypeIds.map((item) => {
        return {
          id: item,
          ratingFrom: filterStore.value.ratingFrom,
          ratingTo: filterStore.value.ratingTo
        }
      })
    })
  )
}

async function updateLocationFromFilterStore(): Promise<void> {
  initFilterStoreFromLocalStorage()
  const { data, error } = await useAccosBarApi()
  if (data != null) {
    const locationName = data?.find((loc: IAccosBarDto) => loc.areaId === filterStore.value.areaId)
    location.value = { areaId: filterStore.value.areaId, name: locationName?.name }
  }
}

function updateFilterStoreFromAccosBarStore(): void {
  filterStore.value.rooms = accosBarData.value.rooms
  filterStore.value.startDate = accosBarData.value.date ? accosBarData.value.date[0] : null
  filterStore.value.endDate = accosBarData.value.date ? accosBarData.value.date[1] : null
}

export function initFilterStoreFromLocalStorage(): void {
  const localFilterStorage = localStorage.getItem(FiltersLocalStorage)
  if (localFilterStorage != null) {
    //ignoreUpdates otherwise endless loop if two apps are on the same page
    watchFilterStore.ignoreUpdates(() => {
      const x = JSON.parse(localFilterStorage)
      filterStore.value = x
    })

    ratingsFilterSelectedIgnoreUpdates(() => {
      if (filterStore.value.ratingFrom && filterStore.value.ratingTo) {
        ratingsFilterSelectedIgnoreUpdates(() => {
          ratingsFilterSelected.value = Array.from(
            { length: filterStore.value.ratingTo - filterStore.value.ratingFrom + 1 },
            (v, i) => filterStore.value.ratingFrom + i
          )
        })
      }
    })
  }
}

const watchRatingsFilterSelected = watchIgnorable(ratingsFilterSelected, (r, oldR) => {
  if (oldR.length > r.length && oldR[0] === r[0] && oldR[oldR.length - 1] === r[r.length - 1]) {
    r = oldR.filter((x) => !r.includes(x))
  }
  const max = r && r.length ? Math.max(...r) : 5.5
  const min = r && r.length ? Math.min(...r) : 0
  filterStore.value.ratingFrom = min
  filterStore.value.ratingTo = max === 4 ? 4.5 : max === 3 ? 3.5 : max

  watchRatingsFilterSelected.ignoreUpdates(() => {
    ratingsFilterSelected.value = r && r.length ? Array.from({ length: max - min + 1 }, (v, i) => min + i) : []
  })
})

export const ratingsFilterSelectedIgnoreUpdates = watchRatingsFilterSelected.ignoreUpdates

function calcRatingSelectedFromFilter(filter: IFilterStoreInterface): number[] {
  return filterStore.value.ratingTo && filterStore.value.ratingFrom
    ? Array.from({ length: filterStore.value.ratingTo - filterStore.value.ratingFrom + 1 }, (v, i) => filterStore.value.ratingFrom + i)
    : []
}

function getFilterCount(filter: IFilterStoreInterface): void {
  let count = 0

  const rating = calcRatingSelectedFromFilter(filter)

  if (filter.themeId) count++
  if (rating) count += rating.length
  if (filter.accotypeIds) count += filter.accotypeIds.length
  if (filter.boardingIds) count += filter.boardingIds.length
  if (filter.amenityIds) count += filter.amenityIds.length

  filterCount.value = count
}

watch(
  () => filterStore,
  (filter) => {
    if (filter.value) {
      getFilterCount(filter.value)
    }
  },
  { deep: true }
)

watch(
  //done to keep pets.value.hasPets and filterStore.value.amenityIds in sync
  () => filterStore.value.amenityIds,
  () => {
    if (filterStore.value.amenityIds.includes(PetsAllowedAmenityId)) {
      pets.value.hasPets = true
    } else {
      pets.value.hasPets = false
    }
  }
)
